import { Button } from "@/components/ui/button";
import { ArtworkCard } from "@/components/ArtworkCard";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { ArrowRight, Sparkles, Shield, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProductCardSkeleton } from "@/components/LoadingSpinner";
import { combineStructuredData, getOrganizationData, getWebSiteData, getItemListData } from "@/lib/structuredData";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthProvider";

export default function Home() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const {
    data: artworks
  } = useQuery({
    queryKey: ["featured-artworks"],
    queryFn: async () => {
      const {
        data: arts,
        error
      } = await supabase.from("artworks").select(`
          *,
          artwork_images (image_url)
        `).eq("is_active", true).eq("approval_status", "approved").order("created_at", {
        ascending: false
      }).limit(6);
      if (error) throw error;

      // Fetch public artist profiles separately to ensure they work for logged-out users
      const artistIds = Array.from(new Set((arts || []).map((a: any) => a.artist_id).filter(Boolean)));
      let profilesMap = new Map<string, any>();
      if (artistIds.length > 0) {
        const { data: profiles } = await supabase
          .from("public_profiles")
          .select("id, username, full_name")
          .in("id", artistIds);
        (profiles || []).forEach((p: any) => profilesMap.set(p.id, p));
      }

      const enriched = (arts || []).map((a: any) => ({
        ...a,
        public_profiles: profilesMap.get(a.artist_id) || null,
      }));

      return enriched;
    }
  });

  const structuredData = combineStructuredData(
    getOrganizationData(),
    getWebSiteData(),
    ...(artworks && artworks.length > 0 
      ? [getItemListData(artworks.map((a: any) => ({
          id: a.id,
          title: a.title,
          description: a.description,
          price: a.price,
          image_url: a.artwork_images?.[0]?.image_url,
          artist_name: a.public_profiles?.full_name || a.public_profiles?.username
        })), t('home.featured.title'))] 
      : [])
  );

  return <div className="min-h-screen bg-background">
      <SEOHead 
        title={t('home.seo.title')} 
        description={t('home.seo.description')} 
        canonicalUrl="/" 
        keywords={["konst", "konstverk", "original", "tryck", "svenska konstnärer", "köp konst online"]} 
        structuredData={structuredData} 
      />
      <Navbar />
      
      {/* Hero Section - Mobile First */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-muted/30 to-accent/20 py-12 sm:py-20 md:py-32 lg:py-40 border-b border-border/50">
        <div className="container relative z-10 px-6 sm:px-4">
          <div className="mx-auto max-w-4xl text-center animate-fade-in">
            <div className="mb-4 sm:mb-6 md:mb-8 inline-flex items-center gap-2 rounded-full border border-border/50 bg-card/80 backdrop-blur-sm px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 text-sm md:text-base font-medium shadow-silver">
              <Sparkles className="h-4 w-4 shrink-0" />
              <span>{t('home.hero.badge')}</span>
            </div>
            <h1 className="mb-4 sm:mb-6 md:mb-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-bold tracking-tight animate-slide-up leading-[1.1] px-2">
              {t('home.hero.title')}{" "}
              <span className="relative inline-block">
                <span className="relative z-10">{t('home.hero.titleHighlight')}</span>
                <span className="absolute inset-0 bg-gradient-to-r from-accent/30 via-accent/20 to-accent/30 blur-xl"></span>
              </span>
            </h1>
            <p className="mb-6 sm:mb-8 md:mb-10 text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed animate-fade-in max-w-2xl mx-auto" style={{
            animationDelay: "0.2s"
          }}>
              {t('home.hero.description')}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4 animate-fade-in max-w-md mx-auto sm:max-w-none" style={{
            animationDelay: "0.4s"
          }}>
              <Link to="/explore" className="w-full sm:w-auto">
                <Button size="lg" className="group text-base font-medium w-full sm:w-auto h-12 sm:h-11">
                  {t('home.hero.exploreCta')}
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/auth" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="text-base font-medium w-full sm:w-auto h-12 sm:h-11">
                  {t('home.hero.sellCta')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Decorative gradient orbs - subtle silver */}
        <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-accent/10 blur-3xl opacity-40" />
        <div className="absolute -bottom-32 -left-32 h-[500px] w-[500px] rounded-full bg-accent/10 blur-3xl opacity-40" />
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] opacity-20"></div>
      </section>

      {/* Features Section - Mobile First */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-background to-muted/20 border-b border-border/50">
        <div className="container px-6 sm:px-4">
          <div className="grid gap-10 sm:gap-12 md:grid-cols-3">
            <div className="flex flex-col items-center text-center group animate-fade-in">
              <div className="mb-4 sm:mb-5 md:mb-6 rounded-full bg-gradient-to-br from-accent/10 to-accent/5 p-6 md:p-7 border border-accent/20 group-hover:shadow-silver transition-all duration-300 group-hover:scale-105 backdrop-blur-sm">
                <Shield className="h-10 w-10 md:h-11 md:w-11 text-foreground" />
              </div>
              <h3 className="mb-2 sm:mb-3 text-xl sm:text-2xl font-serif font-semibold">{t('home.features.payments.title')}</h3>
              <p className="text-base leading-relaxed text-muted-foreground max-w-xs mx-auto">
                {t('home.features.payments.description')}
              </p>
            </div>
            <div className="flex flex-col items-center text-center group animate-fade-in" style={{
            animationDelay: "0.1s"
          }}>
              <div className="mb-4 sm:mb-5 md:mb-6 rounded-full bg-gradient-to-br from-accent/10 to-accent/5 p-6 md:p-7 border border-accent/20 group-hover:shadow-silver transition-all duration-300 group-hover:scale-105 backdrop-blur-sm">
                <TrendingUp className="h-10 w-10 md:h-11 md:w-11 text-foreground" />
              </div>
              <h3 className="mb-2 sm:mb-3 text-xl sm:text-2xl font-serif font-semibold">{t('home.features.support.title')}</h3>
              <p className="text-base leading-relaxed text-muted-foreground max-w-xs mx-auto">{t('home.features.support.description')}</p>
            </div>
            <div className="flex flex-col items-center text-center group animate-fade-in" style={{
            animationDelay: "0.2s"
          }}>
              <div className="mb-4 sm:mb-5 md:mb-6 rounded-full bg-gradient-to-br from-accent/10 to-accent/5 p-6 md:p-7 border border-accent/20 group-hover:shadow-silver transition-all duration-300 group-hover:scale-105 backdrop-blur-sm">
                <Sparkles className="h-10 w-10 md:h-11 md:w-11 text-foreground" />
              </div>
              <h3 className="mb-2 sm:mb-3 text-xl sm:text-2xl font-serif font-semibold">{t('home.features.prints.title')}</h3>
              <p className="text-base leading-relaxed text-muted-foreground max-w-xs mx-auto">
                {t('home.features.prints.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Artworks - Mobile First */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 pb-24 sm:pb-32 md:pb-40">
        <div className="container px-6 sm:px-4">
          <div className="mb-8 sm:mb-10 md:mb-12 text-center animate-fade-in">
            <h2 className="mb-3 sm:mb-4 md:mb-6 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold">{t('home.featured.title')}</h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {t('home.featured.description')}
            </p>
          </div>
          <div className="grid gap-4 sm:gap-5 md:gap-6 grid-cols-2 lg:grid-cols-4">
            {!artworks ?
          // Loading state
          Array.from({
            length: 6
          }).map((_, i) => <ProductCardSkeleton key={i} />) : artworks.length > 0 ? artworks.map(artwork => <ArtworkCard key={artwork.id} id={artwork.id} title={artwork.title} artistName={(artwork.public_profiles as any)?.username || artwork.public_profiles?.full_name || t('common.unknownArtist')} price={Number(artwork.price)} imageUrl={artwork.artwork_images?.[0]?.image_url || "/placeholder.svg"} category={artwork.category} artworkType={artwork.artwork_type} stockQuantity={artwork.stock_quantity} />) : <div className="col-span-full text-center py-12">
                <p className="text-muted-foreground">{t('home.featured.empty')}</p>
              </div>}
          </div>
          <div className="mt-8 sm:mt-10 md:mt-12 text-center animate-fade-in">
            <Link to="/explore" className="inline-block w-full sm:w-auto">
              <Button size="lg" variant="outline" className="border-2 w-full sm:w-auto h-12">
                {t('home.featured.viewAll')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section - Only for logged out users */}
      {!user && (
        <section className="py-12 sm:py-16 md:py-20 bg-muted/30 border-b border-border/50">
          <div className="container px-6 sm:px-4">
            <NewsletterSignup />
          </div>
        </section>
      )}

      {/* CTA Section - Mobile First */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-primary text-primary-foreground relative overflow-hidden border-y border-border/50">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="container relative px-6 sm:px-4">
          <div className="mx-auto max-w-3xl text-center animate-fade-in">
            <h2 className="mb-3 sm:mb-4 md:mb-6 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold">{t('home.cta.title')}</h2>
            <p className="mb-6 sm:mb-8 md:mb-10 text-base md:text-lg leading-relaxed opacity-90 max-w-xl mx-auto">
              {t('home.cta.description')}
            </p>
            <Link to="/auth" className="inline-block w-full sm:w-auto max-w-md sm:max-w-none mx-auto">
              <Button size="lg" variant="secondary" className="text-base font-medium shadow-premium w-full h-12">
                {t('home.cta.button')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
}
